import { useState } from "react";
import { useLocation } from "wouter";
import { useUser } from "@/lib/user";
import { api, HttpError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowRight, Sparkles, Star, Lock, Check } from "lucide-react";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// The two free quests reinforced on the squeeze page — mirrors QUEST_META
// but hardcoded here so this page never breaks if the catalog changes.
const FREE_QUESTS = [
  { id: "Q00", title: "What kind of founder are you?", xp: 100 },
  { id: "Q01", title: "Escape Your 9-5", xp: 200 },
];

export default function SqueezePage() {
  const [, navigate] = useLocation();
  const { setUser } = useUser();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(e) {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!EMAIL_RE.test(trimmed)) {
      setError("Please enter a valid email");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      // Email-only signup — backend derives name from prefix for new users.
      const newUser = await api.login(trimmed);
      setUser(newUser);
      // Squeeze intent: send them straight into Quest 0. QuestPage handles
      // returning users gracefully (loads existing progress if any).
      navigate("/quest/0");
    } catch (err) {
      if (err instanceof HttpError) {
        setError(err.body?.detail?.[0]?.msg || err.body?.detail || "Something went wrong. Try again.");
      } else {
        setError(err.message || "Something went wrong. Try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main
      data-testid="squeeze-page"
      className="min-h-screen w-full flex justify-center px-5 py-9 sm:py-14"
      style={{ fontFamily: "'Space Grotesk', ui-sans-serif, system-ui, sans-serif" }}
    >
      <div className="w-full max-w-[520px]">
        {/* Eyebrow */}
        <span
          data-testid="squeeze-eyebrow"
          className="inline-flex items-center gap-1.5 font-mono text-[11px] tracking-[0.2em] uppercase text-amber-300 border border-zinc-800 rounded-full px-3.5 py-1.5 mb-6"
        >
          <Sparkles className="h-3 w-3" />
          FREE FOUNDER ASSESSMENT
        </span>

        {/* Headline */}
        <h1
          data-testid="squeeze-headline"
          className="text-[29px] sm:text-[35px] lg:text-[39px] leading-[1.12] font-bold tracking-tight text-zinc-50"
        >
          Still climbing a ladder that{" "}
          <em className="italic font-bold text-amber-400 not-italic-fallback">isn&apos;t yours?</em>
        </h1>

        {/* Lede */}
        <p
          data-testid="squeeze-lede"
          className="mt-4 text-[15px] sm:text-base leading-[1.6] text-zinc-400"
        >
          Take two free quests. An AI mentor assesses your financial readiness and your idea,
          then builds your personal{" "}
          <strong className="text-zinc-100 font-medium">Founder Mode roadmap</strong> — the sequence
          to leave your 9-5 without gambling your savings.{" "}
          <strong className="text-zinc-100 font-medium">10 minutes. No credit card.</strong>
        </p>

        {/* Proofline */}
        <div
          data-testid="squeeze-proofline"
          className="mt-5 flex items-center gap-2.5 text-[13px] text-zinc-500"
        >
          <span className="flex items-center gap-0.5 text-amber-400" aria-label="5 out of 5 stars">
            {[0, 1, 2, 3, 4].map((i) => (
              <Star key={i} className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
            ))}
          </span>
          <span>39 Amazon reviews · 599 founders in the community</span>
        </div>

        {/* Form card */}
        <form
          onSubmit={handleSubmit}
          data-testid="squeeze-form"
          className="mt-4 bg-[#16140F] border border-zinc-800 rounded-2xl p-5 sm:p-6"
          noValidate
        >
          <label
            htmlFor="squeeze-email-input"
            className="block font-mono text-[10.5px] tracking-[0.16em] uppercase text-zinc-500 mb-2"
          >
            EMAIL
          </label>
          <Input
            id="squeeze-email-input"
            data-testid="squeeze-email-input"
            type="email"
            inputMode="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Your email"
            disabled={submitting}
            className="bg-zinc-950/60 border-zinc-800 text-zinc-100 placeholder:text-zinc-600 h-12 text-base"
          />

          <Button
            type="submit"
            disabled={submitting}
            data-testid="squeeze-submit-btn"
            className="mt-3.5 w-full h-12 bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold text-[15px] gap-2 group"
          >
            {submitting ? "Starting…" : (
              <>
                Build My Free Roadmap
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </>
            )}
          </Button>

          <p className="mt-3 text-center text-[11.5px] text-zinc-500">
            Free forever · No credit card ·{" "}
            <a href="#/privacy" className="underline hover:text-zinc-300">Privacy</a>
          </p>
          {error && (
            <p data-testid="squeeze-error" className="mt-3 text-center text-[12.5px] text-red-400">
              {error}
            </p>
          )}
        </form>

        {/* Trust marker (matches landing) */}
        <p
          data-testid="squeeze-trust-marker"
          className="mt-3 text-[12px] text-zinc-400 inline-flex items-center gap-2"
        >
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-400 animate-pulse" />
          Join <span className="font-semibold text-amber-300">599+ founders</span> on the quest
        </p>

        {/* Creator credit */}
        <p className="mt-6 text-[13px] text-zinc-500 leading-relaxed">
          Created by <strong className="text-zinc-100 font-medium">Praveen Singh V</strong> — founder
          of the 1000 Founders Community and author of{" "}
          <em className="italic">The Founder Mode</em>.
        </p>

        {/* Two free quests preview */}
        <p className="mt-8 font-mono text-[11px] tracking-[0.2em] uppercase text-zinc-500">
          START WITH THESE TWO — FREE
        </p>
        <div
          data-testid="squeeze-quests"
          aria-label="Your first two free quests"
          className="mt-3 space-y-2"
        >
          {FREE_QUESTS.map((q) => (
            <div
              key={q.id}
              className="flex items-center gap-3 rounded-lg border border-zinc-800 bg-zinc-950/40 px-3.5 py-3"
            >
              <span className="font-mono text-[11px] text-zinc-500">{q.id}</span>
              <span className="flex-1 text-[14.5px] text-zinc-200 truncate">{q.title}</span>
              <span className="font-mono text-[11px] text-zinc-500">+{q.xp}</span>
              <span className="font-mono text-[10px] tracking-wider uppercase rounded-full border border-amber-500/40 bg-amber-500/[0.08] text-amber-300 px-2 py-0.5">
                FREE
              </span>
            </div>
          ))}
          <p className="mt-3 text-[12.5px] text-zinc-500 leading-relaxed flex items-start gap-2">
            <Lock className="h-3.5 w-3.5 text-zinc-600 shrink-0 mt-0.5" />
            <span>
              Finish both free quests to unlock the full path — 11 advanced quests, your complete
              roadmap, and the book — with the Founder Pass.
            </span>
          </p>
        </div>

        {/* Roadmap explainer */}
        <p className="mt-8 text-[13.5px] text-zinc-500 leading-relaxed">
          Developed over 5+ years alongside{" "}
          <strong className="text-zinc-100 font-medium">599 founders</strong> inside the 1000
          Founders Community. The quests don&apos;t just hand you theory —{" "}
          <strong className="text-zinc-100 font-medium">
            they question you, score you, and build a roadmap specific to your situation.
          </strong>
        </p>

        {/* Testimonials */}
        <blockquote
          data-testid="squeeze-testimonial-1"
          className="mt-6 rounded-xl border border-zinc-800 bg-zinc-950/40 p-5"
        >
          <p className="text-[14.5px] text-zinc-300 leading-[1.55]">
            <strong className="text-zinc-50 font-medium">
              &ldquo;It&apos;s not just theory — it feels like a mentor guiding you step by step.&rdquo;
            </strong>
          </p>
          <cite className="mt-2 block font-mono text-[11px] not-italic text-zinc-500">
            — Amazon reviewer · Kindle edition · ★★★★★
          </cite>
        </blockquote>

        <blockquote
          data-testid="squeeze-testimonial-2"
          className="mt-3 rounded-xl border border-zinc-800 bg-zinc-950/40 p-5"
        >
          <p className="text-[14.5px] text-zinc-300 leading-[1.55]">
            <strong className="text-zinc-50 font-medium">
              &ldquo;One of the best communities out there for founders. The book is excellent —
              not theoretical, very practical. I&apos;d recommend it to all founders.&rdquo;
            </strong>
          </p>
          <cite className="mt-2 block font-mono text-[11px] not-italic text-zinc-500">
            — Ekdali · Verified Purchase · ★★★★★
          </cite>
        </blockquote>

        {/* Small "no spam" foot — kept above the global Footer for tight visual grouping */}
        <p className="mt-7 text-center text-[11px] text-zinc-500 leading-relaxed">
          <Check className="inline h-3 w-3 text-emerald-400 mr-1 -mt-0.5" />
          No spam. Unsubscribe anytime.
        </p>
      </div>
    </main>
  );
}
